import { useState, Fragment } from 'react'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Toolbar from '@mui/material/Toolbar'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import Menu from '@mui/material/Menu'
import MenuIcon from '@mui/icons-material/Menu'
import Divider from '@mui/material/Divider'
import Container from '@mui/material/Container'
import CssBaseline from '@mui/material/CssBaseline'
import Button from '@mui/material/Button'
import Slide from '@mui/material/Slide'
import useScrollTrigger from '@mui/material/useScrollTrigger'
import MenuItem from '@mui/material/MenuItem'
import Link from '@mui/material/Link'
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag'
import { HashLink } from 'react-router-hash-link'

import textBrandImage from '../../assets/brand-text.svg'

import data from '../../data'

interface SubMenuItem {
  label: string
  value: string
}

interface PageConfig {
  type: string
  value: string
  hideDesktop?: boolean
  hideMobile?: boolean
  isBeta?: boolean
  submenu?: SubMenuItem[]
}

interface Props {
  window?: () => Window
  children: React.ReactElement
}

const pages: Record<string, PageConfig> = {
  Home: {
    type: 'page',
    value: '/',
    hideDesktop: true
  },
  Schedule: {
    type: 'hash',
    value: '/#schedule'
  },
  About: {
    type: 'page',
    value: '/about',
    submenu: [
      { label: 'Our Vision', value: '/about' },
      { label: 'Instructors', value: '/instructors' },
      { label: 'Blue Belt Requirements', value: '/bluebelt' },
      { label: 'Rules', value: '/rules' },
      { label: 'FAQ', value: '/faq' }
    ]
  },
  Programs: {
    type: 'hash',
    value: '/#programs'
  },
  Competition: {
    type: 'page',
    value: '/competition-team',
    submenu: [
      { label: 'Sponsored Athletes', value: '/competition-team' },
      { label: 'Regional Competitions', value: '/competitions' }
    ]
  },
  Facility: {
    type: 'hash',
    value: '/#location'
  },
  Membership: {
    type: 'href',
    value: '/enroll/options'
  }
}

function HideOnScroll (props: Props) {
  const { children } = props
  const trigger = useScrollTrigger()
  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  )
}

// const settings = ['Profile', 'Account', 'Dashboard', 'Logout'];

function ResponsiveAppBar () {
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null)
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null)

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorElNav(event.currentTarget)
  }

  const handleCloseNavMenu = (): void => {
    setAnchorElNav(null)
  }

  const handleSubmenuOpen = (page: string): void => {
    setOpenSubmenu(page)
  }

  const handleSubmenuClose = (): void => {
    setOpenSubmenu(null)
  }

  const BrandBox = () => (<Box sx={{ justifyContent: { xs: 'center', md: 'flex-start' } }}>
      <Link href="/">
        <Box p={2} id="main" sx={{ height: { xs: '100px', md: '100px' } }}>
          <img src={textBrandImage} alt="The Black Dog Jiu-Jitsu Company" width="111" height="68" style={{ height: '68px', width: 'auto' }}/>
        </Box>
      </Link>
    </Box>)

  const IconMenu = () => (
      <Box sx={{
        flexGrow: 0,
        justifyContent: 'flex-start',
        display: { xs: 'flex', md: 'none' }
      }}>
        <IconButton
          size="large"
          aria-label="account of current user"
          aria-controls="menu-appbar"
          aria-haspopup="true"
          onClick={handleOpenNavMenu}
          color="inherit"
        >
          <MenuIcon />
        </IconButton>
        <Menu
          id="menu-appbar"
          anchorEl={anchorElNav}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
          open={Boolean(anchorElNav)}
          onClose={handleCloseNavMenu}
          sx={{
            display: { xs: 'block', md: 'none' },
            marginTop: '65px',
            '& .MuiPaper-root': {
              maxHeight: 'calc(100vh - 100px)',
              overflowY: 'auto',
            },
          }}
        >
          {
            Object.keys(pages).map((page: any) => {
              if (pages[page].hideMobile === true) {
                return (<Fragment key={page}></Fragment>)
              }
              
              // Handle items with submenu - show header and indented submenu items in mobile
              if (pages[page].submenu) {
                return (
                  <Fragment key={page}>
                    <MenuItem sx={{ pointerEvents: 'none' }}>
                      <Typography>
                        {page}
                      </Typography>
                    </MenuItem>
                    {pages[page].submenu!.map((item: SubMenuItem) => (
                      <MenuItem 
                        key={item.value} 
                        onClick={handleCloseNavMenu}
                        sx={{ pl: 4 }}
                      >
                        <Typography>
                          <Link href={item.value}>{item.label}</Link>
                        </Typography>
                      </MenuItem>
                    ))}
                  </Fragment>
                )
              }
              
              return (
                <MenuItem key={pages[page].value} onClick={handleCloseNavMenu}>
                  <Typography textAlign="center" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {
                      pages[page].type === 'hash'
                        ? (<Link component={HashLink} to={pages[page].value}>{page}</Link>)
                        : (<Link href={pages[page].value}>{page}</Link>)
                    }
                    {pages[page].isBeta && (
                      <Typography
                        component="span"
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: 500,
                          color: 'text.secondary',
                          textTransform: 'none',
                          fontStyle: 'italic',
                        }}
                      >
                        Beta
                      </Typography>
                    )}
                  </Typography>
                </MenuItem>
              )
            })
          }
          <Divider />
          <MenuItem key="store" onClick={handleCloseNavMenu}>
            <Typography textAlign="center">
              <Link href={data.store.url} target="_blank">Store</Link>
            </Typography>
          </MenuItem>
        </Menu>
      </Box>
  )

  const TextMenu = () => (<Box sx={{ flexGrow: 1, justifyContent: 'end', display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
      {
        Object.keys(pages).map((page) => {
          if (pages[page].hideDesktop === true) {
            return (<Fragment key={page}></Fragment>)
          }
          
          // Handle items with submenu
          if (pages[page].submenu) {
            return (
              <Box 
                key={page} 
                sx={{ position: 'relative' }}
                onMouseEnter={() => handleSubmenuOpen(page)}
                onMouseLeave={handleSubmenuClose}
              >
                <Button
                  sx={{
                    minWidth: '0px',
                    my: 2,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {page}
                </Button>
                <Box
                  sx={{
                    position: 'absolute',
                    top: 'calc(100% - 16px)',
                    left: 0,
                    backgroundColor: '#fff',
                    borderRadius: 1,
                    boxShadow: 3,
                    minWidth: '200px',
                    py: 1,
                    zIndex: 1300,
                    display: openSubmenu === page ? 'block' : 'none'
                  }}
                >
                  {pages[page].submenu!.map((item) => (
                    <MenuItem 
                      key={item.value} 
                      component={Link}
                      href={item.value}
                      sx={{ 
                        textDecoration: 'none',
                        color: 'text.primary',
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        }
                      }}
                    >
                      {item.label}
                    </MenuItem>
                  ))}
                </Box>
              </Box>
            )
          }
          
          if (pages[page].type === 'hash') {
            return (
              <Button
                component={HashLink}
                to={pages[page].value}
                key={page}
                onClick={handleCloseNavMenu}
                sx={{
                  minWidth: '0px',
                  my: 2,
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                {page}
                {pages[page].isBeta && (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '0.55rem',
                      fontWeight: 400,
                      color: 'rgba(255,255,255,0.5)',
                      textTransform: 'none',
                      fontStyle: 'italic',
                      lineHeight: 1,
                    }}
                  >
                    Beta
                  </Typography>
                )}
              </Button>
            )
          }
          return (
            <Button
              href={pages[page].value}
              key={page}
              onClick={handleCloseNavMenu}
              sx={{
                minWidth: '0px',
                my: 2,
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              {page}
              {pages[page].isBeta && (
                <Typography
                  component="span"
                  sx={{
                    fontSize: '0.55rem',
                    fontWeight: 400,
                    color: 'rgba(255,255,255,0.5)',
                    textTransform: 'none',
                    fontStyle: 'italic',
                    lineHeight: 1,
                  }}
                >
                  Beta
                </Typography>
              )}
            </Button>
          )
        })
      }
      <Button
        href={data.store.url}
        key="store"
        onClick={handleCloseNavMenu}
        sx={{ my: 2, color: 'white', display: 'block' }}
      >
        Store
      </Button>
    </Box>)

  return (
    <Fragment>
      <CssBaseline />
      <HideOnScroll>
        <AppBar position='fixed'>
          <Container maxWidth="xl">
            <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
              <IconMenu />
              <BrandBox />
              <TextMenu />
              <Box sx={{
                width: '48px',
                display: { xs: 'flex', md: 'none' },
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                <Link href={data.store.url} color="secondary" target="_blank" aria-label="Store">
                  <ShoppingBagIcon />
                </Link>
              </Box>
            </Toolbar>
          </Container>
        </AppBar>
      </HideOnScroll>
      <Toolbar sx={{ height: '100px' }}/>
    </Fragment>
  )
}
export default ResponsiveAppBar
